import axios from 'axios'
import config from '@/config.js'
import exportConvert from '@/store/export.convert.js'
import {
  getStaticDetail,
  readOnlyError,
} from '@/services/data-source.js'
import {
  normalizeArticle,
  unwrapArticlePayload,
} from '@/services/article-data.js'

const API_URL = config.apiUrl
const API_URL_CLIENTSIDE = config.apiUrlClientSide
let MAIN_URL = API_URL
if (config.staticData) {
  MAIN_URL = API_URL_CLIENTSIDE
}

let articleRequestSequence = 0

const state = {
  articles: [],
  article: null
}

const mutations = {
  setArticles(state, articles){
    state.articles = articles
  },
  deleteArticle(state){
    state.article = null
  },
  setArticle(state, article){
    state.article = article
  }
}

const getters = {
  // getArticle: (state) => (id) => state.articles.filter(article => article.id == id),
}

const actions = {
  searchArticles({ commit }, data){
    axios
      .get(`${MAIN_URL}/articles/autocomplete?query=${data.query}&dictionaries=${data.dictionaries}`)
      .then(r => r.data)
      .then(articles => {
        commit('setArticles', articles)
      })
  },
  async loadArticle({ commit, dispatch }, id){
    const requestSequence = ++articleRequestSequence
    commit('setArticle', null)
    try {
      const response = config.staticData
        ? await getStaticDetail('article', id)
        : await axios.get(`${API_URL}/article/${id}`)
      let article = unwrapArticlePayload(response.data)
      if (Array.isArray(article)) {
        article = await exportConvert.convert('article', article)
      }
      article = normalizeArticle(article)
      if (requestSequence !== articleRequestSequence) return null
      commit('setArticle', article)
      return article
    } catch (error) {
      if (requestSequence === articleRequestSequence) {
        commit('setArticle', null)
        const notFound = error.response && error.response.status === 404
        dispatch('addNotification', {
          type: 'danger',
          text: notFound
            ? `Article ${id} was not found`
            : error.message || `Article ${id} could not be loaded`,
        })
      }
      throw error
    }
  },
  async loadSimpleArticle(_, id){
    const response = config.staticData
      ? await getStaticDetail('article', id)
      : await axios.get(`${API_URL}/article/${id}`)
    let article = unwrapArticlePayload(response.data)
    if (Array.isArray(article)) {
      article = await exportConvert.convert('article', article)
    }
    return normalizeArticle(article)
  },
  saveArticleTree(_, data){
    if (!config.editable) return readOnlyError('Saving an article tree')
    return new Promise((resolve) => {
      axios
        .post(`${API_URL}/article/${data.articleId}/tree`, { tree: data.tree, manualNumbers: data.manualNumbers })
        .then(response => {
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  moveLemmaToArticle(_, data){
    if (!config.editable) return readOnlyError('Moving a lemma')
    return new Promise((resolve) => {
      axios
        .post(`${API_URL}/article/${data.articleId}/lemma/${data.lemmaId}/move`, {'newArticleId': data.newArticleId})
        .then(response => {
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  addLemmasToArticle(_, data){
    if (!config.editable) return readOnlyError('Adding lemmata to an article')
    return new Promise((resolve) => {
      axios
        .post(`${API_URL}/article/${data.articleId}/add_lemmas`, data)
        .then(response => {
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  saveArticle(_, article){
    if (!config.editable) return readOnlyError('Saving an article')
    return new Promise((resolve) => {
      axios
        .post(`${API_URL}/article/${article.id}`, article)
        .then(response => {
          this.dispatch('addNotification', {
            type: 'success',
            text: `Article saved`
          })
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  deleteArticle({ commit }, obj){
    if (!config.editable) return readOnlyError('Deleting an article')
    return new Promise((resolve) => {
      axios
        .delete(`${API_URL}/article/${obj.articleId}`)
        .then(response => {
          commit('deleteArticle')
          this.dispatch('addNotification', {
            type: 'success',
            text: `Article deleted`
          })
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  createArticle(_, data){
    if (!config.editable) return readOnlyError('Creating an article')
    return new Promise((resolve) => {
      axios
        .post(`${API_URL}/article`, data)
        .then(response => {
          this.dispatch('addNotification', {
            type: 'success',
            text: `Article created`
          })
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
